import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { FormEditor, formShape } from '@techchange/components';

import { RESOURCE_TYPES } from '../../../../constants';

const propTypes = {

	/**
	 * The unique ID for the content.
	 */
	id: PropTypes.string,

	/**
	 * The type of the content.
	 */
	type: PropTypes.oneOf([RESOURCE_TYPES.FORM]).isRequired,

	/**
	 * The title of the content.
	 */
	title: PropTypes.string,

	/**
	 * Additional data for the content.
	 */
	data: PropTypes.shape({

		/**
		 * The unique ID for the associated form.
		 */
		form_id: PropTypes.string,

	}),

	/**
	 * The width of the content.
	 */
	width: PropTypes.number,

	/**
	 * The scope of this content, i.e. "session", "course", "organization", etc.
	 */
	scope: PropTypes.string,

	/**
	 * The unique scope ID of this content.
	 */
	scopeId: PropTypes.string,

	/**
	 * The associated form (if any).
	 */
	form: formShape,

	/**
	 * Function to handle cancelling the content editor.
	 */
	onCancel: PropTypes.func.isRequired,

	/**
	 * Function to handle saving the content editor.
	 */
	onSave: PropTypes.func.isRequired,

	/**
	 * Callback function to handle changing the width of the content.
	 */
	onWidthChange: PropTypes.func,

};

const ContentEditor = (props) => {

	const {
		id,
		type,
		title,
		width,
		scope,
		scopeId,
		form,
		onCancel,
		onSave,
		onWidthChange,
	} = props;

	const [hasError, setHasError] = useState(false);

	/**
	 * Handles saving the content editor for the form.
	 *
	 * @param {object} formData  The form data to save.
	 */
	const handleSave = (formData) => {

		// Parse the form data to build the up-to-date data for the overall content
		const contentData = {
			id,
			type,
			title: formData.name,
			width: formData.width,
			data: {
				form_id: formData.id,
			},
			// Keep the multi-language resource content in-sync with the form
			resource_contents: formData.form_contents.map((content) => ({
				language: content.language,
				title: content.name,
				data: {
					form_id: formData.id,
				},
			})),
			deleted_resource_contents: formData.deleted_form_contents.map((content) => ({
				language: content.language,
			})),
		};

		setHasError(false);

		onSave(contentData, formData).catch((error) => {

			console.error(error);

			setHasError(true);

		});

	};

	/**
	 * Handles real-time edits to the width of the content.
	 *
	 * @param {object} formData  The current state of the form data.
	 */
	const handleWidthChange = (formData) => {

		// Parse the form data to build the up-to-date data for the overall content
		const contentData = {
			id,
			type,
			title: formData.name,
			width: formData.width,
			data: {
				form_id: formData.id,
				// Preserve the state of the form data in case the width change triggers re-mounts
				form: { ...formData },
			},
		};

		onWidthChange(id, contentData);

	};

	let initialForm;

	// Check if editing an existing form
	if (form) {

		initialForm = {
			...form,
			width,
		};

	// Otherwise, create the initial data for a new form
	} else {

		initialForm = {
			type,
			name: title,
			allow_editing_submissions: true,
			allow_multiple_submissions: false,
			width,
			scope,
			scope_id: scopeId,
			form_fields: [],
			form_sections: [],
		};

	}

	return (
		<div className="tc-content-builder-form-editor">
			<FormEditor
				form={initialForm}
				hasError={hasError}
				onCancel={onCancel}
				onSave={handleSave}
				onWidthChange={handleWidthChange} />
		</div>
	);

};

ContentEditor.propTypes = propTypes;

export default ContentEditor;
